import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@web/components/ui/button.tsx";
import { cn } from "@web/lib/utils.ts";
import { closeDetailSheet } from "@web/features/detail/sheetUrl.ts";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";
import { useDetailStore } from "@web/stores/detail.ts";
import { useUiStore } from "@web/stores/ui.ts";

function syncSheetToTop() {
  const top = useBrowseTrailStore.getState().stack.at(-1);
  if (!top) {
    closeDetailSheet();
    return;
  }
  useDetailStore.getState().setTarget({ type: top.type, id: top.id });
  useUiStore.getState().setSheetOpen(true);
}

/**
 * Sticky in-sheet trail: back / forward + breadcrumbs.
 * Dive-local only — closing the sheet clears it; grid opens reset it.
 */
export function TrailBar() {
  const stack = useBrowseTrailStore((s) => s.stack);
  const forward = useBrowseTrailStore((s) => s.forward);
  const drawer = useBrowseTrailStore((s) => s.drawer);
  const jumpTo = useBrowseTrailStore((s) => s.jumpTo);
  const back = useBrowseTrailStore((s) => s.back);
  const goForward = useBrowseTrailStore((s) => s.goForward);
  const setTarget = useDetailStore((s) => s.setTarget);
  const setSheetOpen = useUiStore((s) => s.setSheetOpen);

  const canBack = drawer !== null || stack.length > 1;
  const canForward = forward.length > 0 && drawer === null;

  if (stack.length === 0 && !drawer) return null;

  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-line bg-panel-elevated px-2 py-1.5">
      <Button
        variant="ghost"
        size="icon-sm"
        className="size-7 shrink-0"
        aria-label="Back"
        disabled={!canBack}
        onClick={() => {
          const hadDrawer = useBrowseTrailStore.getState().drawer !== null;
          back();
          if (hadDrawer) return;
          syncSheetToTop();
        }}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="size-7 shrink-0"
        aria-label="Forward"
        disabled={!canForward}
        onClick={() => {
          goForward();
          syncSheetToTop();
        }}
      >
        <ChevronRight className="size-4" />
      </Button>

      <nav
        aria-label="Exploration trail"
        className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5"
      >
        {stack.map((item, index) => {
          const isLast = index === stack.length - 1 && !drawer;
          return (
            <span
              key={`${item.type}:${item.id}:${index}`}
              className="flex min-w-0 items-center gap-0.5"
            >
              {index > 0 && <span className="px-0.5 text-faint">›</span>}
              <button
                type="button"
                className={cn(
                  "max-w-36 truncate rounded-md px-1.5 py-0.5 text-xs font-medium",
                  isLast
                    ? "text-text"
                    : "text-muted hover:bg-row-hover hover:text-text",
                )}
                onClick={() => {
                  jumpTo(index);
                  setTarget({ type: item.type, id: item.id });
                  setSheetOpen(true);
                }}
              >
                {item.label}
              </button>
            </span>
          );
        })}
        {drawer && (
          <span className="flex min-w-0 items-center gap-0.5">
            {stack.length > 0 && <span className="px-0.5 text-faint">›</span>}
            <span className="truncate px-1.5 py-0.5 text-xs font-medium text-accent">
              shared with {drawer.withLabel}
            </span>
          </span>
        )}
      </nav>
    </div>
  );
}
